import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { api } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import ChatPanel from '../../components/chat/ChatPanel';
import NotificationBell from '../../components/common/NotificationBell';
import {
  ArrowLeft, MapPin, Clock, Phone, MessageCircle, Star, AlertTriangle,
  CheckCircle, Truck, Home as HomeIcon, Wrench, User, FileText,
  Calendar, DollarSign, Navigation, Shield, ChevronDown, ChevronUp, Send, Share2, RefreshCw, Plus,
  Locate, ExternalLink, Download, Info
} from 'lucide-react';
import UserAvatar from '../../components/common/UserAvatar';
import UnifiedMap from '../../components/common/UnifiedMap';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const statusSteps = [
  { key: 'pending', label: 'Pending', icon: Clock, color: 'text-yellow-600' },
  { key: 'accepted', label: 'Accepted', icon: CheckCircle, color: 'text-blue-600' },
  { key: 'on_the_way', label: 'On the Way', icon: Truck, color: 'text-purple-600' },
  { key: 'arrived', label: 'Arrived', icon: MapPin, color: 'text-indigo-600' },
  { key: 'in_progress', label: 'In Progress', icon: Wrench, color: 'text-orange-600' },
  { key: 'pending_completion', label: 'Awaiting Confirmation', icon: Clock, color: 'text-amber-600' },
  { key: 'completed', label: 'Completed', icon: CheckCircle, color: 'text-green-600' },
];

// Warranty Countdown Component
function WarrantyCountdown({ warrantyEndDate }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, expired: false });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const end = new Date(warrantyEndDate);
      const now = new Date();
      const diff = end - now;
      
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, expired: true });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setTimeLeft({ days, hours, minutes, expired: false });
    };
    
    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [warrantyEndDate]);
  
  if (timeLeft.expired) {
    return (
      <div className="bg-stone-100 rounded-lg p-3">
        <p className="text-sm text-stone-600 text-center font-medium">Warranty Expired</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white/80 rounded-lg p-3">
      <p className="text-xs text-amber-700 text-center mb-2 font-medium">Warranty Time Remaining</p>
      <div className="flex justify-center gap-3">
        <div className="text-center">
          <div className="bg-amber-500 text-white rounded-lg px-3 py-2 min-w-[50px]">
            <span className="text-xl font-bold">{timeLeft.days}</span>
          </div>
          <p className="text-xs text-amber-700 mt-1">Days</p>
        </div>
        <div className="text-center">
          <div className="bg-amber-500 text-white rounded-lg px-3 py-2 min-w-[50px]">
            <span className="text-xl font-bold">{timeLeft.hours}</span>
          </div>
          <p className="text-xs text-amber-700 mt-1">Hours</p>
        </div>
        <div className="text-center">
          <div className="bg-amber-500 text-white rounded-lg px-3 py-2 min-w-[50px]">
            <span className="text-xl font-bold">{timeLeft.minutes}</span>
          </div>
          <p className="text-xs text-amber-700 mt-1">Min</p>
        </div>
      </div>
      <p className="text-xs text-amber-600 text-center mt-2">
        Until: {new Date(warrantyEndDate).toLocaleDateString()}
      </p>
    </div>
  );
}

export default function BookingTracking() {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { user } = useAuth();

  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [providerLocation, setProviderLocation] = useState(null);
  const [customerLocation, setCustomerLocation] = useState(null);
  const [myLocation, setMyLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [showAddFeeDialog, setShowAddFeeDialog] = useState(false);
  const [newFees, setNewFees] = useState([{ description: '', amount: '' }]);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportData, setReportData] = useState({ reason: '', description: '' });
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [isShareingLocation, setIsSharingLocation] = useState(false);
  const [showConfirmCompletionDialog, setShowConfirmCompletionDialog] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [isConfirmingCompletion, setIsConfirmingCompletion] = useState(false);
  const [customerWalletBalance, setCustomerWalletBalance] = useState(0);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
  const [showInsuranceClaimDialog, setShowInsuranceClaimDialog] = useState(false);
  const [insuranceClaimData, setInsuranceClaimData] = useState({ claim_type: '', description: '', estimated_damage: '' });
  const [isSubmittingClaim, setIsSubmittingClaim] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [showWarrantyClaimDialog, setShowWarrantyClaimDialog] = useState(false);
  const [warrantyClaimData, setWarrantyClaimData] = useState({ reason: '', description: '' });
  const [isSubmittingWarrantyClaim, setIsSubmittingWarrantyClaim] = useState(false);
  const wsRef = useRef(null);
  const watchIdRef = useRef(null);

  const fetchDetails = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/details`);
      setDetails(res.data);
      if (res.data.provider_location) {
        setProviderLocation(res.data.provider_location);
      }
      if (res.data.customer_location) {
        setCustomerLocation(res.data.customer_location);
      }
    } catch (error) {
      console.error('Failed to fetch booking details:', error);
      toast.error('Booking not found');
    } finally {
      setIsLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchDetails();
    const interval = setInterval(fetchDetails, 15000);
    return () => clearInterval(interval);
  }, [fetchDetails]);

  // Location sharing
  const startLocationSharing = useCallback(() => {
    if (watchIdRef.current) return;
    const token = localStorage.getItem('token');
    const isProvider = user?.role === 'provider';
    const endpoint = isProvider ? 'provider-location' : 'customer-location';

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setMyLocation({ latitude, longitude });
        try {
          await axios.put(
            `${API}/bookings/${bookingId}/${endpoint}?latitude=${latitude}&longitude=${longitude}`,
            {}, { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (e) { console.error('Location update failed:', e); }
      },
      (err) => { console.error('Geolocation error:', err); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    setIsSharingLocation(true);
  }, [bookingId, user?.role]);

  const stopLocationSharing = useCallback(() => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsSharingLocation(false);
  }, []);

  // Auto-start location sharing for active bookings
  useEffect(() => {
    if (!details || !user) return;
    const { booking } = details;
    const isActive = ['accepted', 'on_the_way', 'arrived', 'in_progress'].includes(booking.status);
    if (isActive && navigator.geolocation) {
      startLocationSharing();
    }
    return () => stopLocationSharing();
  }, [details?.booking?.status, user, startLocationSharing, stopLocationSharing]);

  // WebSocket connection
  useEffect(() => {
    if (!user?.id) return;
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/${user.id}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'booking_status_update' && data.booking_id === bookingId) {
          fetchDetails();
          toast.info(`Booking status: ${data.status.replace('_', ' ')}`);
        }
        if (data.type === 'provider_location' && data.booking_id === bookingId) {
          setProviderLocation({ latitude: data.latitude, longitude: data.longitude });
        }
        if (data.type === 'customer_location' && data.booking_id === bookingId) {
          setCustomerLocation({ latitude: data.latitude, longitude: data.longitude });
        }
      } catch (e) {
        console.warn('BookingTracking: failed to parse websocket message', e);
      }
    };

    return () => ws.close();
  }, [user?.id, bookingId, fetchDetails]);

  const handleSubmitReview = async () => {
    setIsSubmittingReview(true);
    try {
      await api.createReview({ booking_id: bookingId, rating: reviewData.rating, comment: reviewData.comment });
      toast.success('Review submitted! Thank you.');
      setShowReviewDialog(false);
      fetchDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await api.updateBookingStatus(bookingId, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      fetchDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Handle customer cancellation
  const handleCancelBooking = async () => {
    if (!cancelReason) {
      toast.error('Please select a reason for cancellation');
      return;
    }
    setIsCancelling(true);
    try {
      await api.updateBookingStatus(bookingId, { 
        status: 'cancelled', 
        cancellation_reason: cancelReason 
      });
      toast.success('Booking cancelled successfully');
      setShowCancelDialog(false);
      setCancelReason('');
      fetchDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleShare = async () => {
    try {
      const res = await api.createShareLink(bookingId);
      const shareUrl = `${window.location.origin}/track/${res.data.share_token}`;
      if (navigator.share) {
        await navigator.share({ title: 'Track my booking', text: `Track my HandyBayani booking status`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Tracking link copied to clipboard!');
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast.error('Failed to share');
    }
  };

  const handleAddFees = async () => {
    const validFees = newFees.filter(f => f.description && f.amount > 0);
    if (validFees.length === 0) { toast.error('Add at least one fee'); return; }
    try {
      await axios.post(`${API}/bookings/${bookingId}/additional-fees`, validFees.map(f => ({ description: f.description, amount: parseFloat(f.amount) })));
      toast.success('Additional fees added');
      setShowAddFeeDialog(false);
      setNewFees([{ description: '', amount: '' }]);
      fetchDetails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add fees'); }
  };

  const handleSubmitReport = async () => {
    if (!reportData.reason || !reportData.description) {
      toast.error('Please fill in all fields');
      return;
    }
    setIsSubmittingReport(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/reports/user`, {
        reported_user_id: details?.provider_id,
        booking_id: bookingId,
        reason: reportData.reason,
        description: reportData.description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report submitted successfully. Our team will review it.');
      setShowReportDialog(false);
      setReportData({ reason: '', description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  // Handle insurance claim submission
  const handleSubmitInsuranceClaim = async () => {
    if (!insuranceClaimData.claim_type || !insuranceClaimData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmittingClaim(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/insurance/claims`, {
        booking_id: bookingId,
        claim_type: insuranceClaimData.claim_type,
        description: insuranceClaimData.description,
        estimated_damage: parseFloat(insuranceClaimData.estimated_damage) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Insurance claim submitted successfully. Our team will review it within 24-48 hours.');
      setShowInsuranceClaimDialog(false);
      setInsuranceClaimData({ claim_type: '', description: '', estimated_damage: '' });
      fetchDetails(); // Refresh to show claim status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit insurance claim');
    } finally {
      setIsSubmittingClaim(false);
    }
  };

  const handleSubmitWarrantyClaim = async () => {
    if (!warrantyClaimData.reason || !warrantyClaimData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmittingWarrantyClaim(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/warranty-claims`, {
        booking_id: bookingId,
        reason: warrantyClaimData.reason,
        description: warrantyClaimData.description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Warranty claim submitted! We will review it shortly.');
      setShowWarrantyClaimDialog(false);
      setWarrantyClaimData({ reason: '', description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit warranty claim');
    } finally {
      setIsSubmittingWarrantyClaim(false);
    }
  };


  // Fetch customer wallet balance when showing confirmation dialog
  const fetchWalletBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/wallet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerWalletBalance(res.data.balance || 0);
    } catch (e) {
      setCustomerWalletBalance(0);
    }
  };

  // Handle customer confirming job completion
  const handleConfirmCompletion = async () => {
    setIsConfirmingCompletion(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API}/bookings/${bookingId}/confirm-completion`,
        { payment_method: selectedPaymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.requires_redirect && res.data.checkout_url) {
        // Online payment - redirect to PayMongo
        window.location.href = res.data.checkout_url;
        return;
      }
      
      toast.success('Booking completed! Thank you for using HandyBayani.');
      setShowConfirmCompletionDialog(false);
      fetchDetails(); // Refresh booking details
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to confirm completion');
    } finally {
      setIsConfirmingCompletion(false);
    }
  };

  // Get direction to the destination with optional origin
  // For providers: origin = provider's current location, destination = service location
  // For customers: origin = current location (default), destination = provider location
  const getDirectionUrl = (destLat, destLng, originLat = null, originLng = null) => {
    let url = `https://www.google.com/maps/dir/?api=1&destination=${destLat},${destLng}`;
    if (originLat && originLng) {
      url += `&origin=${originLat},${originLng}`;
    }
    return url;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <p className="text-slate-500">Booking not found</p>
      </div>
    );
  }

  const { booking, customer, provider, service, category, timeline, review } = details;
  const isCompleted = booking.status === 'completed';
  const isCancelled = booking.status === 'cancelled';
  const isProvider = user?.role === 'provider';
  const currentStep = statusSteps.findIndex(s => s.key === booking.status);
  const hasMap = booking.address?.latitude && booking.address?.longitude;
  const isActive = ['accepted', 'on_the_way', 'arrived', 'in_progress'].includes(booking.status);
  
  // Customer can cancel if booking is pending or accepted
  const canCustomerCancel = !isProvider && ['pending', 'finding_provider', 'accepted'].includes(booking.status);

  // Provider status action
  const providerActions = {
    pending: { next: 'accepted', label: 'Accept Job' },
    accepted: { next: 'on_the_way', label: 'Start Journey' },
    on_the_way: { next: 'arrived', label: "I've Arrived" },
    arrived: { next: 'in_progress', label: 'Start Work' },
    in_progress: { next: 'pending_completion', label: 'Complete Job' },
  };
  
  // Check if booking needs customer confirmation
  const needsCustomerConfirmation = booking.status === 'pending_completion' && !isProvider;

  const otherUser = isProvider ? customer : provider;

  // Build map markers
  const mapMarkers = [];
  if (hasMap) {
    mapMarkers.push({ lat: booking.address.latitude, lng: booking.address.longitude, color: 'red', label: `Service Location: ${booking.address.full_address}` });
  }
  if (providerLocation?.latitude) {
    mapMarkers.push({ lat: providerLocation.latitude, lng: providerLocation.longitude, color: 'blue', label: 'Provider Location' });
  }
  if (customerLocation?.latitude && isProvider) {
    mapMarkers.push({ lat: customerLocation.latitude, lng: customerLocation.longitude, color: 'green', label: 'Customer Location' });
  }

  // Determine the "other user's" location for Get Direction
  const otherUserLocation = isProvider ? customerLocation : providerLocation;

  // Format address helper
  const formatAddress = (addr) => {
    if (!addr) return null;
    const parts = [addr.full_address];
    if (addr.barangay) parts.push(`Brgy. ${addr.barangay}`);
    if (addr.city) parts.push(addr.city);
    if (addr.zip_code) parts.push(addr.zip_code);
    return parts.join(', ');
  };

  // Chat view - Full screen
  if (showChat) {
    return (
      <div className="fixed inset-0 z-[100] bg-white">
        <ChatPanel
          bookingId={bookingId}
          otherUser={otherUser}
          onClose={() => setShowChat(false)}
          wsRef={wsRef}
          onMessagesRead={() => {
            setDetails(prev => prev ? { ...prev, chat_count: 0 } : prev);
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-8">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 px-4 py-3 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(isProvider ? '/provider/jobs' : '/bookings')}
              className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="font-bold text-slate-900 text-base font-[Poppins]">Booking Details</h1>
              <p className="text-xs text-slate-500">#{booking.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={handleShare}
              className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center"
              data-testid="share-booking-btn"
            >
              <Share2 className="w-5 h-5 text-slate-600" />
            </button>
            {booking.provider_id && !isCompleted && !isCancelled && (
              <button
                onClick={() => setShowChat(true)}
                className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center relative"
                data-testid="open-chat-btn"
              >
                <MessageCircle className="w-5 h-5 text-amber-700" />
                {details.chat_count > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {details.chat_count}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="px-4 py-4 space-y-4 max-w-lg mx-auto">
        {/* Status Banner */}
        <div className={`rounded-2xl p-4 ${
          isCancelled ? 'bg-red-50 border border-red-200' :
          isCompleted ? 'bg-green-50 border border-green-200' :
          'bg-amber-50 border border-amber-200'
        }`} data-testid="status-banner">
          <div className="flex items-center gap-3 mb-3">
            {isCancelled ? <AlertTriangle className="w-5 h-5 text-red-600" /> :
             isCompleted ? <CheckCircle className="w-5 h-5 text-green-600" /> :
             <Clock className="w-5 h-5 text-amber-600" />}
            <span className={`font-semibold text-sm ${
              isCancelled ? 'text-red-900' : isCompleted ? 'text-green-900' : 'text-amber-900'
            }`}>
              {booking.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          </div>

          {/* Status Progress */}
          {!isCancelled && (
            <div className="flex items-center gap-1">
              {statusSteps.map((step, idx) => (
                <div key={step.key} className={`h-1.5 flex-1 rounded-full ${
                  idx <= currentStep ? 'bg-slate-900' : 'bg-stone-200'
                }`} />
              ))}
            </div>
          )}
        </div>

        {/* Map with both locations */}
        {hasMap && !isCancelled && (
          <div data-testid="booking-map-section">
            <UnifiedMap
              center={[booking.address.latitude, booking.address.longitude]}
              zoom={14}
              height="220px"
              markers={mapMarkers}
            />
            {/* Location sharing indicator + Get Direction button */}
            <div className="flex items-center justify-between mt-2 gap-2">
              {isActive && (
                <div className="flex items-center gap-1.5 text-xs">
                  {isShareingLocation ? (
                    <span className="flex items-center gap-1 text-green-700 bg-green-50 px-2 py-1 rounded-full">
                      <Locate className="w-3 h-3 animate-pulse" /> Sharing location
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={startLocationSharing} data-testid="start-sharing-location-btn">
                      <Locate className="w-3 h-3 mr-1" /> Share Location
                    </Button>
                  )}
                </div>
              )}
              {/* Provider: Get Direction from provider's current location to service location */}
              {isProvider && hasMap && isActive && (
                <a
                  href={getDirectionUrl(
                    booking.address.latitude, 
                    booking.address.longitude,
                    providerLocation?.latitude, 
                    providerLocation?.longitude
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
                  data-testid="get-direction-to-service-btn"
                >
                  <Navigation className="w-3.5 h-3.5" /> Navigate to Service
                </a>
              )}
              {/* Customer: Get Direction to provider's location (if sharing) */}
              {!isProvider && otherUserLocation?.latitude && (
                <a
                  href={getDirectionUrl(otherUserLocation.latitude, otherUserLocation.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
                  data-testid="get-direction-to-provider-btn"
                >
                  <Navigation className="w-3.5 h-3.5" /> Track Provider
                </a>
              )}
              {/* Customer: Get Direction to service location (if provider not sharing) */}
              {!isProvider && !otherUserLocation?.latitude && hasMap && isActive && (
                <a
                  href={getDirectionUrl(booking.address.latitude, booking.address.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-slate-600 hover:bg-slate-700 text-white text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
                  data-testid="get-direction-service-btn"
                >
                  <Navigation className="w-3.5 h-3.5" /> View Location
                </a>
              )}
            </div>
          </div>
        )}

        {/* Provider Action Button */}
        {isProvider && providerActions[booking.status] && (
          <div className={`flex gap-2 ${hasMap ? 'mt-5' : ''}`}>
            <Button
              className="flex-1 h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
              onClick={() => handleStatusUpdate(providerActions[booking.status].next)}
              data-testid="provider-action-btn"
            >
              {providerActions[booking.status].label}
            </Button>
            {!isCompleted && !isCancelled && (
              <Button
                variant="outline"
                className="h-12"
                onClick={() => setShowAddFeeDialog(true)}
                data-testid="add-fee-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Fee
              </Button>
            )}
          </div>
        )}

        {/* Customer Confirmation Button - shown when status is pending_completion */}
        {needsCustomerConfirmation && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mt-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Job Completed!</h3>
                <p className="text-sm text-slate-600 mt-1">
                  The provider has marked this job as complete. Please confirm and choose your payment method.
                </p>
                <Button
                  className="mt-3 w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                  onClick={() => {
                    fetchWalletBalance();
                    setShowConfirmCompletionDialog(true);
                  }}
                  data-testid="confirm-completion-btn"
                >
                  Confirm Job Completion
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Customer Cancel Button - shown when booking is pending/accepted */}
        {canCustomerCancel && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-slate-900">Need to cancel?</h3>
                <p className="text-sm text-slate-500">You can cancel this booking while it's still pending.</p>
              </div>
              <Button
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => setShowCancelDialog(true)}
                data-testid="cancel-booking-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Cancel Booking
              </Button>
            </div>
          </div>
        )}

        {/* Tabs: Details / Timeline / Chat */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat">
              Chat {details.chat_count > 0 && `(${details.chat_count})`}
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4 mt-4">
            {/* Provider/Customer Card */}
            {otherUser && (
              <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="other-user-card">
                <h3 className="font-semibold text-slate-900 text-sm mb-3">
                  {isProvider ? 'Customer' : 'Your Provider'}
                </h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center">
                      <UserAvatar
                        src={otherUser.profile_image}
                        name={otherUser.name}
                        role={isProvider ? 'customer' : 'provider'}
                        size="lg"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 text-sm">{otherUser.name}</p>
                        {otherUser.face_verified && (
                          <Shield className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                      {provider && !isProvider && (
                        <div className="flex items-center gap-2 mt-0.5">
                          <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                          <span className="text-xs text-slate-600">{provider.rating || '0'} ({provider.total_jobs || 0} jobs)</span>
                        </div>
                      )}
                      {otherUser.phone && (
                        <p className="text-xs text-slate-500 mt-0.5">{otherUser.phone}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {otherUser.phone && (
                      <a href={`tel:${otherUser.phone}`} className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center" data-testid="call-btn">
                        <Phone className="w-4 h-4 text-green-600" />
                      </a>
                    )}
                    <button
                      onClick={() => setShowChat(true)}
                      className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center"
                      data-testid="message-btn"
                    >
                      <MessageCircle className="w-4 h-4 text-blue-600" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Addresses Card */}
            <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="addresses-card">
              <h3 className="font-semibold text-slate-900 text-sm mb-3">Addresses</h3>
              <div className="space-y-3">
                {/* Service Location */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-500 uppercase">Service Location</p>
                    <p className="text-sm text-slate-900 mt-0.5">{formatAddress(booking.address) || booking.address?.full_address}</p>
                  </div>
                </div>
                {/* Customer Address */}
                {details.customer_address && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <HomeIcon className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-500 uppercase">Customer Address</p>
                      <p className="text-sm text-slate-900 mt-0.5">{formatAddress(details.customer_address)}</p>
                    </div>
                  </div>
                )}
                {/* Provider Address */}
                {details.provider_address && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Wrench className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-500 uppercase">Provider Address</p>
                      <p className="text-sm text-slate-900 mt-0.5">{formatAddress(details.provider_address)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Service Info */}
            <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="service-info">
              <h3 className="font-semibold text-slate-900 text-sm mb-3">Service Details</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Wrench className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{service?.name || 'Service'}</p>
                    <p className="text-xs text-slate-500">{category?.name || 'Category'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">Schedule</p>
                    <p className="text-xs text-slate-500">{booking.scheduled_date} at {booking.scheduled_time}</p>
                  </div>
                </div>
                {service?.estimated_duration && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Est. Duration</p>
                      <p className="text-xs text-slate-500">{service.estimated_duration} minutes</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Service Warranty Info */}
            {booking.has_warranty && booking.warranty_days > 0 && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 shadow-sm border border-amber-200" data-testid="warranty-info">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900 text-sm">Service Warranty</h3>
                    <p className="text-xs text-amber-700">{booking.warranty_days}-day coverage</p>
                  </div>
                </div>
                
                {booking.warranty_end_date && (
                  <WarrantyCountdown warrantyEndDate={booking.warranty_end_date} />
                )}
                
                {booking.warranty_description && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <p className="text-xs font-medium text-amber-800 mb-1">Coverage Details:</p>
                    <p className="text-xs text-amber-700">{booking.warranty_description}</p>
                  </div>
                )}
                
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <p className="text-xs text-amber-700 flex items-start gap-1">
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    This warranty ensures quality service. If there's any issue with the work, contact the provider within the warranty period.
                  </p>
                  {booking.status === 'completed' && booking.warranty_end_date && new Date(booking.warranty_end_date) > new Date() && (
                    <Button
                      size="sm"
                      className="mt-3 w-full bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => setShowWarrantyClaimDialog(true)}
                      data-testid="file-warranty-claim-btn"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      File Warranty Claim
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Payment Summary */}
            <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="payment-summary">
              <h3 className="font-semibold text-slate-900 text-sm mb-3">Payment Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Base Price</span>
                  <span className="text-slate-900">Php {booking.base_price?.toFixed(2)}</span>
                </div>
                {booking.add_ons_price > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Add-ons</span>
                    <span className="text-slate-900">Php {booking.add_ons_price?.toFixed(2)}</span>
                  </div>
                )}
                {booking.additional_fees?.length > 0 && (
                  <>
                    <div className="border-t border-dashed border-stone-200 my-2" />
                    <p className="text-xs font-medium text-slate-500">Additional Fees:</p>
                    {booking.additional_fees.map(fee => (
                      <div key={fee.id} className="flex justify-between text-sm">
                        <span className="text-slate-500">{fee.description}</span>
                        <span className="text-slate-900">Php {fee.amount?.toFixed(2)}</span>
                      </div>
                    ))}
                  </>
                )}
                {booking.customer_insurance_fee > 0 && (
                  <div className="flex justify-between text-sm text-blue-700 bg-blue-50 -mx-4 px-4 py-1">
                    <span className="flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Service Insurance
                    </span>
                    <span>Php {booking.customer_insurance_fee?.toFixed(2)}</span>
                  </div>
                )}
                {isProvider && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Commission ({(booking.commission_rate * 100).toFixed(0)}%)</span>
                      <span className="text-red-600">-Php {booking.commission_amount?.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-stone-200 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-semibold text-slate-900 text-sm">Your Earnings</span>
                        <span className="font-bold text-lg text-green-600">Php {booking.provider_earnings?.toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                )}
                {!isProvider && (
                  <div className="border-t border-stone-200 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-slate-900 text-sm">Total</span>
                      <span className="font-bold text-lg text-amber-600">Php {booking.total_price?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-2">Payment method: Cash on completion</p>
              </div>
            </div>

            {/* Booking Notes */}
            {booking.notes && (
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <h3 className="font-semibold text-slate-900 text-sm mb-2">Notes</h3>
                <p className="text-sm text-slate-600">{booking.notes}</p>
              </div>
            )}

            {/* Review Section */}
            {review && (
              <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="review-section">
                <h3 className="font-semibold text-slate-900 text-sm mb-3">Review</h3>
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />
                  ))}
                  <span className="text-sm text-slate-700 ml-2">{review.rating}/5</span>
                </div>
                {review.comment && <p className="text-sm text-slate-600">{review.comment}</p>}
                <p className="text-xs text-slate-400 mt-2">{new Date(review.created_at).toLocaleDateString()}</p>
              </div>
            )}

            {/* Actions */}
            {isCompleted && !review && !isProvider && (
              <Button
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                onClick={() => setShowReviewDialog(true)}
                data-testid="review-btn"
              >
                <Star className="w-5 h-5 mr-2" />
                Rate & Review
              </Button>
            )}

            {/* View Service Invoice */}
            {isCompleted && (
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={() => setShowInvoiceDialog(true)}
                data-testid="view-invoice-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Service Invoice
              </Button>
            )}

            {/* Rebook Button */}
            {isCompleted && !isProvider && (
              <Button
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                onClick={() => navigate(`/book/${booking.service_id}`, { 
                  state: { 
                    rebook: true,
                    category_id: booking.category_id,
                    address: booking.address,
                    notes: booking.notes
                  }
                })}
                data-testid="rebook-btn"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Book Again
              </Button>
            )}
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="mt-4">
            <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="timeline-section">
              <h3 className="font-semibold text-slate-900 text-sm mb-4">Status Timeline</h3>
              {timeline.length === 0 ? (
                <p className="text-sm text-slate-400">No timeline data yet</p>
              ) : (
                <div className="space-y-0">
                  {timeline.map((entry, idx) => {
                    const step = statusSteps.find(s => s.key === entry.status);
                    const StepIcon = step?.icon || Clock;
                    const isLast = idx === timeline.length - 1;
                    return (
                      <div key={entry.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isLast ? 'bg-slate-900' : 'bg-stone-200'}`}>
                            <StepIcon className={`w-4 h-4 ${isLast ? 'text-white' : 'text-slate-500'}`} />
                          </div>
                          {idx < timeline.length - 1 && <div className="w-0.5 h-8 bg-stone-200" />}
                        </div>
                        <div className="pb-4">
                          <p className={`font-medium text-sm ${isLast ? 'text-slate-900' : 'text-slate-600'}`}>
                            {entry.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(entry.created_at).toLocaleString()}
                          </p>
                          {entry.notes && <p className="text-xs text-slate-500 mt-1">{entry.notes}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Timestamps Summary */}
              <div className="mt-4 pt-4 border-t border-stone-100 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Created</span>
                  <span className="text-slate-700">{new Date(booking.created_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Last Updated</span>
                  <span className="text-slate-700">{new Date(booking.updated_at).toLocaleString()}</span>
                </div>
                {booking.completed_at && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Completed</span>
                    <span className="text-green-700 font-medium">{new Date(booking.completed_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat" className="mt-4">
            {booking.provider_id ? (
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-200" style={{ height: '400px' }}>
                <ChatPanel 
                  bookingId={bookingId} 
                  otherUser={otherUser} 
                  wsRef={wsRef}
                  onMessagesRead={() => {
                    // Clear the chat count in local state when messages are read
                    setDetails(prev => prev ? { ...prev, chat_count: 0 } : prev);
                  }}
                />
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                <MessageCircle className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Chat will be available once a provider is assigned</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Report Issue - Available for all bookings */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <button 
            className="flex items-center gap-2 text-red-600 font-medium text-sm" 
            data-testid="report-issue-btn"
            onClick={() => setShowReportDialog(true)}
          >
            <AlertTriangle className="w-4 h-4" />
            Report an Issue
          </button>
        </div>

        {/* Insurance Claim - Only for bookings with insurance */}
        {booking?.insurance_included && booking?.customer_insurance_fee > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-blue-900 text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Service Guarantee Insurance
                </h4>
                <p className="text-xs text-blue-700 mt-1">
                  Coverage: ₱{(booking.customer_insurance_coverage_limit || 10000).toLocaleString()} • 
                  {booking.insurance_claim_status ? (
                    <span className={`ml-1 ${
                      booking.insurance_claim_status === 'approved' ? 'text-green-600' :
                      booking.insurance_claim_status === 'rejected' ? 'text-red-600' :
                      'text-amber-600'
                    }`}>
                      Claim: {booking.insurance_claim_status}
                    </span>
                  ) : (
                    ' Eligible for claims'
                  )}
                </p>
              </div>
              {!booking.insurance_claim_status && ['completed', 'in_progress', 'pending_completion'].includes(booking.status) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  onClick={() => setShowInsuranceClaimDialog(true)}
                  data-testid="file-insurance-claim-btn"
                >
                  File Claim
                </Button>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rate Your Experience</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <p className="text-sm text-slate-500 mb-3">How was the service?</p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setReviewData(prev => ({ ...prev, rating: star }))}
                    className="p-1"
                    data-testid={`star-${star}`}
                  >
                    <Star className={`w-10 h-10 ${star <= reviewData.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />
                  </button>
                ))}
              </div>
            </div>
            <Textarea
              placeholder="Share your experience (optional)"
              value={reviewData.comment}
              onChange={(e) => setReviewData(prev => ({ ...prev, comment: e.target.value }))}
              className="min-h-[100px]"
              data-testid="review-comment"
            />
            <Button
              className="w-full h-12 bg-slate-900 hover:bg-slate-800"
              onClick={handleSubmitReview}
              disabled={isSubmittingReview}
              data-testid="submit-review-btn"
            >
              {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Additional Fees Dialog */}
      <Dialog open={showAddFeeDialog} onOpenChange={setShowAddFeeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Additional Fees</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-500">Add custom charges for materials, transport, etc.</p>
            {newFees.map((fee, idx) => (
              <div key={`fee-${idx}-${fee.description || ''}`} className="flex gap-2">
                <Input
                  placeholder="Description"
                  value={fee.description}
                  onChange={(e) => setNewFees(prev => prev.map((f, i) => i === idx ? { ...f, description: e.target.value } : f))}
                  className="flex-1 h-10"
                  data-testid={`fee-desc-${idx}`}
                />
                <Input
                  type="number"
                  placeholder="Amount"
                  value={fee.amount}
                  onChange={(e) => setNewFees(prev => prev.map((f, i) => i === idx ? { ...f, amount: e.target.value } : f))}
                  className="w-28 h-10"
                  data-testid={`fee-amount-${idx}`}
                />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setNewFees(prev => [...prev, { description: '', amount: '' }])}>
              <Plus className="w-3 h-3 mr-1" /> Add Line
            </Button>
            <Button className="w-full h-11 bg-slate-900 hover:bg-slate-800" onClick={handleAddFees} data-testid="submit-fees-btn">
              Submit Fees
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Issue Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Report an Issue</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">
              Please describe the issue you're experiencing with this booking. Our support team will review and respond.
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700">Issue Type</label>
              <select
                value={reportData.reason}
                onChange={(e) => setReportData(prev => ({ ...prev, reason: e.target.value }))}
                className="w-full h-11 mt-1.5 px-3 border border-stone-200 rounded-lg text-sm"
                data-testid="report-reason-select"
              >
                <option value="">Select issue type...</option>
                <option value="Service Quality Issue">Service Quality Issue</option>
                <option value="Provider No-Show">Provider No-Show</option>
                <option value="Overcharging">Overcharging</option>
                <option value="Damage to Property">Damage to Property</option>
                <option value="Unprofessional Behavior">Unprofessional Behavior</option>
                <option value="Payment Issue">Payment Issue</option>
                <option value="Safety Concern">Safety Concern</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea
                placeholder="Please describe the issue in detail..."
                value={reportData.description}
                onChange={(e) => setReportData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1.5 min-h-[100px]"
                data-testid="report-description"
              />
            </div>
            <Button
              className="w-full h-12 bg-red-600 hover:bg-red-700"
              onClick={handleSubmitReport}
              disabled={isSubmittingReport}
              data-testid="submit-report-btn"
            >
              {isSubmittingReport ? 'Submitting...' : 'Submit Report'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Insurance Claim Dialog */}
      <Dialog open={showInsuranceClaimDialog} onOpenChange={setShowInsuranceClaimDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              File Insurance Claim
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 rounded-lg p-3 text-sm">
              <p className="text-blue-900 font-medium">Coverage Details:</p>
              <p className="text-blue-700 text-xs mt-1">
                Maximum coverage: ₱{(booking?.customer_insurance_coverage_limit || 10000).toLocaleString()}
              </p>
              <p className="text-blue-600 text-xs mt-1">
                {booking?.customer_insurance_terms || 'Covers property damage, incomplete service, and provider no-show.'}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Claim Type *</label>
              <select
                value={insuranceClaimData.claim_type}
                onChange={(e) => setInsuranceClaimData(prev => ({ ...prev, claim_type: e.target.value }))}
                className="w-full h-11 mt-1.5 px-3 border border-stone-200 rounded-lg text-sm"
                data-testid="claim-type-select"
              >
                <option value="">Select claim type...</option>
                <option value="property_damage">Property Damage</option>
                <option value="incomplete_service">Incomplete Service</option>
                <option value="provider_no_show">Provider No-Show</option>
                <option value="quality_issue">Quality Issue</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Estimated Damage/Loss Amount (₱)</label>
              <Input
                type="number"
                placeholder="0.00"
                value={insuranceClaimData.estimated_damage}
                onChange={(e) => setInsuranceClaimData(prev => ({ ...prev, estimated_damage: e.target.value }))}
                className="mt-1.5"
                data-testid="claim-amount"
              />
              <p className="text-xs text-slate-500 mt-1">
                Max claimable: ₱{(booking?.customer_insurance_coverage_limit || 10000).toLocaleString()}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Description *</label>
              <Textarea
                placeholder="Please describe what happened and the damage/loss incurred..."
                value={insuranceClaimData.description}
                onChange={(e) => setInsuranceClaimData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1.5 min-h-[100px]"
                data-testid="claim-description"
              />
            </div>
            
            <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-800">
              <p className="font-medium">Important:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Claims must be filed within 48 hours of service completion</li>
                <li>You may be asked to provide photos or evidence</li>
                <li>Our team will review your claim within 24-48 hours</li>
              </ul>
            </div>
            
            <Button
              className="w-full h-12 bg-blue-600 hover:bg-blue-700"
              onClick={handleSubmitInsuranceClaim}
              disabled={isSubmittingClaim}
              data-testid="submit-claim-btn"
            >
              {isSubmittingClaim ? 'Submitting...' : 'Submit Claim'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Cancel Booking
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Are you sure you want to cancel this booking? Please select a reason for cancellation.
            </p>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Reason for Cancellation *</label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full h-11 mt-1.5 px-3 border border-stone-200 rounded-lg text-sm"
                data-testid="cancel-reason-select"
              >
                <option value="">Select a reason...</option>
                <option value="change_of_plans">Change of Plans</option>
                <option value="found_another_provider">Found Another Provider</option>
                <option value="schedule_conflict">Schedule Conflict</option>
                <option value="price_too_high">Price Too High</option>
                <option value="no_longer_needed">Service No Longer Needed</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-800">
              <p className="font-medium">Note:</p>
              <p className="mt-1">Frequent cancellations may affect your account standing. Please only cancel when necessary.</p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11"
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancelReason('');
                }}
              >
                Keep Booking
              </Button>
              <Button
                className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleCancelBooking}
                disabled={isCancelling || !cancelReason}
                data-testid="confirm-cancel-btn"
              >
                {isCancelling ? 'Cancelling...' : 'Cancel Booking'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Completion Dialog */}
      <Dialog open={showConfirmCompletionDialog} onOpenChange={setShowConfirmCompletionDialog}>
        <DialogContent className="max-w-md mx-auto" data-testid="confirm-completion-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Job Completion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Payment Summary */}
            <div className="bg-stone-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Service Fee</span>
                <span className="font-medium">₱{booking?.base_price?.toLocaleString() || 0}</span>
              </div>
              {booking?.add_ons_price > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Add-ons</span>
                  <span className="font-medium">₱{booking?.add_ons_price?.toLocaleString() || 0}</span>
                </div>
              )}
              <div className="flex justify-between text-sm pt-2 border-t border-stone-200">
                <span className="font-semibold text-slate-900">Total Amount</span>
                <span className="font-bold text-lg text-amber-600">₱{booking?.total_price?.toLocaleString() || 0}</span>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="space-y-3">
              <p className="font-semibold text-slate-900 text-sm">Choose Payment Method</p>
              
              {/* Wallet Payment */}
              <button
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  selectedPaymentMethod === 'wallet' 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-stone-200 bg-white hover:border-stone-300'
                }`}
                onClick={() => setSelectedPaymentMethod('wallet')}
                data-testid="payment-wallet-btn"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Wallet Balance</p>
                      <p className="text-xs text-slate-500">Available: ₱{customerWalletBalance?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${
                    selectedPaymentMethod === 'wallet' 
                      ? 'border-amber-500 bg-amber-500' 
                      : 'border-stone-300'
                  }`}>
                    {selectedPaymentMethod === 'wallet' && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>
                {customerWalletBalance < (booking?.total_price || 0) && (
                  <p className="text-xs text-red-500 mt-2">Insufficient balance</p>
                )}
              </button>

              {/* Cash Payment */}
              <button
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  selectedPaymentMethod === 'cash' 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-stone-200 bg-white hover:border-stone-300'
                }`}
                onClick={() => setSelectedPaymentMethod('cash')}
                data-testid="payment-cash-btn"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Pay Cash</p>
                      <p className="text-xs text-slate-500">Pay the provider directly</p>
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${
                    selectedPaymentMethod === 'cash' 
                      ? 'border-amber-500 bg-amber-500' 
                      : 'border-stone-300'
                  }`}>
                    {selectedPaymentMethod === 'cash' && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>
              </button>

              {/* Online Payment */}
              <button
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  selectedPaymentMethod === 'online' 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-stone-200 bg-white hover:border-stone-300'
                }`}
                onClick={() => setSelectedPaymentMethod('online')}
                data-testid="payment-online-btn"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <ExternalLink className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Online Payment</p>
                      <p className="text-xs text-slate-500">GCash, Maya, Card</p>
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${
                    selectedPaymentMethod === 'online' 
                      ? 'border-amber-500 bg-amber-500' 
                      : 'border-stone-300'
                  }`}>
                    {selectedPaymentMethod === 'online' && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>
              </button>
            </div>

            <Button
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold"
              onClick={handleConfirmCompletion}
              disabled={isConfirmingCompletion || (selectedPaymentMethod === 'wallet' && customerWalletBalance < (booking?.total_price || 0))}
              data-testid="complete-payment-btn"
            >
              {isConfirmingCompletion ? 'Processing...' : 'Confirm & Pay'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-md mx-auto max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Service Invoice
            </DialogTitle>
          </DialogHeader>
          
          {booking && (
            <div className="space-y-4 py-2">
              {/* Invoice Header */}
              <div className="text-center pb-4 border-b border-stone-200">
                <h2 className="text-xl font-bold text-slate-900">HandyBayani</h2>
                <p className="text-xs text-slate-500 mt-1">Home Services Marketplace</p>
                <p className="text-sm font-mono text-slate-600 mt-2">
                  Invoice #{booking.id?.slice(0, 8).toUpperCase()}
                </p>
              </div>

              {/* Customer & Provider Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Customer</p>
                  <p className="font-medium text-slate-900">{details?.customer_name || 'Customer'}</p>
                  <p className="text-xs text-slate-500">{details?.customer_email}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Provider</p>
                  <p className="font-medium text-slate-900">{booking.provider_name}</p>
                  <p className="text-xs text-slate-500">{booking.provider_phone}</p>
                </div>
              </div>

              {/* Service Details */}
              <div className="bg-stone-50 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Service</p>
                  <p className="font-semibold text-slate-900">{booking.service_name}</p>
                  <p className="text-xs text-slate-500">{booking.category_name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-slate-400">Date</p>
                    <p className="text-slate-900">{new Date(booking.scheduled_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Time</p>
                    <p className="text-slate-900">{booking.scheduled_time}</p>
                  </div>
                </div>
                {booking.actual_duration_minutes && (
                  <div>
                    <p className="text-xs text-slate-400">Duration</p>
                    <p className="text-slate-900">{Math.floor(booking.actual_duration_minutes / 60)}h {booking.actual_duration_minutes % 60}m</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-400">Location</p>
                  <p className="text-slate-900 text-sm">{typeof booking.address === 'object' ? booking.address?.full_address : booking.address}</p>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="space-y-2">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Pricing Breakdown</p>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Service Fee</span>
                    <span className="text-slate-900">₱{booking.base_price?.toLocaleString() || 0}</span>
                  </div>
                  {booking.add_ons_price > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Add-ons / Additional Fees</span>
                      <span className="text-slate-900">₱{booking.add_ons_price?.toLocaleString()}</span>
                    </div>
                  )}
                  {booking.additional_fees?.length > 0 && booking.additional_fees.map((fee, idx) => (
                    <div key={`addfee-${fee.description}-${fee.amount}`} className="flex justify-between text-sm pl-4">
                      <span className="text-slate-500 text-xs">• {fee.description}</span>
                      <span className="text-slate-600 text-xs">₱{fee.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm pt-2 border-t border-stone-200">
                    <span className="font-semibold text-slate-900">Total Amount</span>
                    <span className="font-bold text-lg text-amber-600">₱{booking.total_price?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-green-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-600 uppercase tracking-wide">Payment Status</p>
                    <p className="font-semibold text-green-700 capitalize">{booking.payment_status || 'Paid'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-green-600 uppercase tracking-wide">Method</p>
                    <p className="font-medium text-green-700 capitalize">{booking.payment_method || 'Cash'}</p>
                  </div>
                </div>
                {booking.completed_at && (
                  <p className="text-xs text-green-600 mt-2">
                    Completed: {new Date(booking.completed_at).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Download Receipt Button */}
              <Button
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                onClick={async () => {
                  setIsDownloadingReceipt(true);
                  try {
                    const res = await api.downloadReceipt(bookingId);
                    const url = URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `receipt_${bookingId.slice(0, 8)}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('Receipt downloaded successfully');
                  } catch (e) {
                    toast.error('Failed to download receipt');
                  } finally {
                    setIsDownloadingReceipt(false);
                  }
                }}
                disabled={isDownloadingReceipt}
                data-testid="download-receipt-btn"
              >
                {isDownloadingReceipt ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900 mr-2"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download Receipt
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Warranty Claim Dialog */}
      <Dialog open={showWarrantyClaimDialog} onOpenChange={setShowWarrantyClaimDialog}>
        <DialogContent className="max-w-md mx-auto" data-testid="warranty-claim-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              File Warranty Claim
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-amber-50 rounded-xl p-3">
              <p className="text-xs text-amber-700">
                Filing a warranty claim will notify the service provider and our admin team. The provider will have a chance to respond before a resolution is made.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Issue Type</label>
              <select
                value={warrantyClaimData.reason}
                onChange={(e) => setWarrantyClaimData(prev => ({ ...prev, reason: e.target.value }))}
                className="w-full h-11 mt-1.5 border border-stone-200 rounded-lg px-3 text-sm"
                data-testid="warranty-claim-reason"
              >
                <option value="">Select issue type...</option>
                <option value="Service not completed properly">Service not completed properly</option>
                <option value="Quality issues with work">Quality issues with work</option>
                <option value="Damage caused during service">Damage caused during service</option>
                <option value="Work broke or failed after service">Work broke or failed after service</option>
                <option value="Missing or incomplete work">Missing or incomplete work</option>
                <option value="Other warranty issue">Other warranty issue</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea
                value={warrantyClaimData.description}
                onChange={(e) => setWarrantyClaimData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the issue in detail..."
                className="min-h-[100px] mt-1.5"
                data-testid="warranty-claim-description"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowWarrantyClaimDialog(false)}>Cancel</Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleSubmitWarrantyClaim}
                disabled={isSubmittingWarrantyClaim || !warrantyClaimData.reason || !warrantyClaimData.description}
                data-testid="submit-warranty-claim-btn"
              >
                {isSubmittingWarrantyClaim ? 'Submitting...' : 'Submit Claim'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
